import { SearchField } from "containers/ExecutionFilter/interfaces";
import { Maybe, ResourceType } from "graphql/generated/graphql";
import { sortAlphabetically } from "utils/sorting";
import { getSearchField, parseFilterSafely, wrapTermWithWildcards } from "utils/utils";

export interface RecordLabelProps {
    resourceName: string;
    label: string;
}

export const getCheckedRecordValue = (
    recordArray: string[],
    totalRecordsNumber: number,
    recordType: string[]
) => {
    const numberOfCheckedRecords = recordType.filter((type) => recordArray.includes(type)).length;
    const isRecordIndeterminate =
        numberOfCheckedRecords > 0 && numberOfCheckedRecords < totalRecordsNumber;
    const isRecordChecked = numberOfCheckedRecords === totalRecordsNumber;

    return isRecordIndeterminate ? null : isRecordChecked;
};

const replaceWithWildcards = (term) => {
    if (
        (term[0] === '"' && term.slice(-1) === '"') ||
        (term[0] === "'" && term.slice(-1) === "'")
    ) {
        return wrapTermWithWildcards(term.slice(1, -1));
    } else if (term.includes("*")) {
        return term.replace(/\*/g, "%");
    } else {
        const value = term.replace(/[^a-zA-Z0-9]/g, "%");
        return wrapTermWithWildcards(value);
    }
};

export const replaceSearchTermWithWildcards = (values) => {
    if (typeof values === "object" && values !== null) {
        if (Array.isArray(values)) {
            values.forEach((item) => replaceSearchTermWithWildcards(item));
        } else {
            if (values["op"] === "like" || values["op"] === "notlike") {
                if (typeof values.value === "string") {
                    values.value = replaceWithWildcards(values.value);
                }
            }

            for (const key in values) {
                values[key] = replaceSearchTermWithWildcards(values[key]);
            }
        }
    }
    return values;
};

export const makeQueryString = (
    queryType: string | null,
    filterFromQuery: any,
    customFilter?: any
) => {
    const filters: any = { and: [] };

    let resourceType: string[] = [];
    let searchField: Maybe<SearchField> = null;

    if (filterFromQuery) {
        const filterOptions = parseFilterSafely(filterFromQuery);

        filterOptions.and = (filterOptions.and || []).filter((field: any) => {
            if (field.field === "resource_type") {
                resourceType = field.value.map((value: string) =>
                    value.toUpperCase().replaceAll(" ", "_")
                );
                return false;
            } else if (field.field === "activity_type") {
                // Selects which query groups run; not a backend filter field.
                return false;
            } else if (field.field === "name") {
                searchField = getSearchField({ term: field.value });
                return false;
            } else {
                return field;
            }
        });

        if (searchField) {
            filterOptions.and.push(searchField);
        }

        if (queryType === "resource_type") {
            filterOptions.and.push({
                field: queryType,
                op: "in",
                value: resourceType,
            });
        }

        filters.and.push(filterOptions);
    }
    if (customFilter) {
        const parsedCustomFilter = parseFilterSafely(customFilter);
        const mappedFilterValues = replaceSearchTermWithWildcards(parsedCustomFilter);
        filters.and.push(mappedFilterValues);
    }

    return filters.and ? JSON.stringify(filters) : null;
};

export const sortTypeListAlphabetically = (typeList: RecordLabelProps[]) =>
    sortAlphabetically([...typeList], (type: RecordLabelProps) => type.label);

export const filterAdvancedResourceTypes = (type: ResourceType) =>
    type !== "WORKSPACE" && type !== "ORGANIZATION";
